/**
 * Central locale config for the next-intl setup (no URL routing — locale comes
 * from the `NEXT_LOCALE` cookie or `Accept-Language`). Keep `SUPPORTED_LOCALES`
 * in sync with:
 *   - the CHECK constraint on `users.locale` (Alembic `s8t9u0vlocale1`)
 *   - the Pydantic `SupportedLocale` literal in `apps/api/app/models/user_schemas.py`
 *   - the per-locale files under `apps/web/messages/`
 */

export const SUPPORTED_LOCALES = [
  'de-DE',
  'de-CH',
  'en',
  'fr',
  'it',
  'pt-BR',
  'es-ES',
  'pl',
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: SupportedLocale = 'de-DE';

export const LOCALE_COOKIE_NAME = 'NEXT_LOCALE';

/** Native labels shown in the User-Settings dropdown. */
export const LOCALE_NATIVE_LABELS: Record<SupportedLocale, string> = {
  'de-DE': 'Deutsch (Deutschland)',
  'de-CH': 'Deutsch (Schweiz)',
  en: 'English',
  fr: 'Français',
  it: 'Italiano',
  'pt-BR': 'Português (Brasil)',
  'es-ES': 'Español',
  pl: 'Polski',
};

export function isSupportedLocale(value: unknown): value is SupportedLocale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Resolve a locale string (typically from `Accept-Language` or a cookie) to one
 * of our 8 supported locales. Falls back to `DEFAULT_LOCALE` when no match is
 * found.
 *
 * Matches the more specific tag first (`de-CH` before `de-DE`), then strips the
 * region suffix and retries — so `de-AT` resolves to `de-DE`.
 */
export function resolveLocale(candidate: string | null | undefined): SupportedLocale {
  if (!candidate) return DEFAULT_LOCALE;
  const normalized = candidate.trim();
  if (isSupportedLocale(normalized)) return normalized;
  const language = normalized.split('-')[0]?.toLowerCase();
  if (language === 'de') return 'de-DE';
  if (language === 'en') return 'en';
  if (language === 'fr') return 'fr';
  if (language === 'it') return 'it';
  if (language === 'pt') return 'pt-BR';
  if (language === 'es') return 'es-ES';
  if (language === 'pl') return 'pl';
  return DEFAULT_LOCALE;
}

/**
 * Parse the `Accept-Language` header (`de-DE;q=0.9,en;q=0.8`) and pick the
 * highest-weighted entry that resolves to a supported locale.
 */
export function pickLocaleFromAcceptLanguage(header: string | null | undefined): SupportedLocale {
  if (!header) return DEFAULT_LOCALE;
  const candidates = header
    .split(',')
    .map((entry) => {
      const [tag, ...params] = entry.trim().split(';');
      const qParam = params.find((p) => p.trim().startsWith('q='));
      const q = qParam ? parseFloat(qParam.split('=')[1]) : 1.0;
      return { tag: tag.trim(), q: Number.isFinite(q) ? q : 1.0 };
    })
    .filter((c) => c.tag.length > 0)
    .sort((a, b) => b.q - a.q);

  for (const { tag } of candidates) {
    const resolved = resolveLocale(tag);
    if (resolved !== DEFAULT_LOCALE || tag.toLowerCase().startsWith('de')) {
      return resolved;
    }
  }
  return DEFAULT_LOCALE;
}
